import { useSearchParams } from 'react-router-dom'
import { useEffect } from 'react'
import { isEmpty } from '@/resources/utils'
import {
  GenericFilterType,
  GenericFilterValue,
  GenericNullableFilterValue,
  TypeMap,
} from './types'
import { ParamTypes, ARRAY_SEPARATOR } from './constants'

interface UrlUpdaterProps<T> {
  filters: T
  typeMap: TypeMap
}

export const useUrlFiltersUpdater = <T extends GenericFilterType>({
  filters,
  typeMap,
}: UrlUpdaterProps<T>) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const filtersContent = JSON.stringify(filters)

  useEffect(() => {
    updateSearchParams<T>(searchParams, filters, typeMap)
    setSearchParams(searchParams)
  }, [filtersContent])
}

const updateSearchParams = <T extends GenericFilterType>(
  searchParams: URLSearchParams,
  filters: T,
  typeMap: TypeMap
) => {
  Object.entries(filters).forEach(([key, value]) => {
    if (hasValue(value, typeMap[key])) {
      searchParams.set(key, getEncodedParam(value))
    } else {
      searchParams.delete(key)
    }
  })
}

const getEncodedParam = (value: GenericFilterValue) => {
  let param: string

  if (Array.isArray(value)) {
    param = value.join(ARRAY_SEPARATOR)
  } else if (typeof value === ParamTypes.OBJECT) {
    param = JSON.stringify(value)
  } else {
    param = String(value)
  }

  return encodeURI(param)
}

const hasValue = (value: GenericNullableFilterValue, type: ParamTypes) => {
  if (isEmpty(value) || isEmpty(type)) return false

  switch (type) {
    case ParamTypes.STRING:
      return (value as string).trim() !== ''
    case ParamTypes.ARRAY:
    case ParamTypes.OBJECT:
      return Object.keys(value as object).length > 0
    case ParamTypes.NUMBER:
    case ParamTypes.BOOLEAN:
      return true
    default:
      return false
  }
}
